import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Stack,
  Rating,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import publicApplicationsService from '../services/publicApplicationsService';

const EvaluationForm = ({ applicationId, onSuccess }) => {
  const [formData, setFormData] = useState({
    overall_rating: 0,
    technical_rating: 0,
    communication_rating: 0,
    experience_rating: 0,
    culture_fit_rating: 0,
    recommendation: '',
    evaluation_notes: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleRatingChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.recommendation) {
      setError('Recommendation is required');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Only send ratings that are greater than 0
      const evaluationData = {
        recommendation: formData.recommendation,
        evaluation_notes: formData.evaluation_notes,
      };

      if (formData.overall_rating > 0) evaluationData.overall_rating = formData.overall_rating;
      if (formData.technical_rating > 0) evaluationData.technical_rating = formData.technical_rating;
      if (formData.communication_rating > 0) evaluationData.communication_rating = formData.communication_rating;
      if (formData.experience_rating > 0) evaluationData.experience_rating = formData.experience_rating;
      if (formData.culture_fit_rating > 0) evaluationData.culture_fit_rating = formData.culture_fit_rating;

      await publicApplicationsService.addEvaluation(applicationId, evaluationData);

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error submitting evaluation:', error);
      setError(error.response?.data?.message || 'Failed to submit evaluation');
    } finally {
      setSubmitting(false);
    }
  };

  const RatingField = ({ label, value, onChange }) => (
    <Box>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
        {label}
      </Typography>
      <Rating
        value={value}
        onChange={(event, newValue) => onChange(newValue)}
        icon={<StarIcon fontSize="large" />}
        emptyIcon={<StarBorderIcon fontSize="large" />}
        sx={{
          '& .MuiRating-iconFilled': { color: '#ffa726' },
          '& .MuiRating-iconHover': { color: '#ff9800' },
        }}
      />
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Ratings Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
          Rating Categories (Optional)
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={4}>
            <RatingField
              label="Overall Rating"
              value={formData.overall_rating}
              onChange={(value) => handleRatingChange('overall_rating', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <RatingField
              label="Technical Skills"
              value={formData.technical_rating}
              onChange={(value) => handleRatingChange('technical_rating', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <RatingField
              label="Communication"
              value={formData.communication_rating}
              onChange={(value) => handleRatingChange('communication_rating', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <RatingField
              label="Experience Level"
              value={formData.experience_rating}
              onChange={(value) => handleRatingChange('experience_rating', value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <RatingField
              label="Culture Fit"
              value={formData.culture_fit_rating}
              onChange={(value) => handleRatingChange('culture_fit_rating', value)}
            />
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Recommendation Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
          Recommendation *
        </Typography>
        <FormControl fullWidth required>
          <InputLabel>Select Recommendation</InputLabel>
          <Select
            name="recommendation"
            value={formData.recommendation}
            onChange={handleChange}
            label="Select Recommendation"
          >
            <MenuItem value="">
              <em>Please select...</em>
            </MenuItem>
            <MenuItem value="shortlist">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#4caf50',
                  }}
                />
                <Typography>Shortlist for Interview</Typography>
              </Stack>
            </MenuItem>
            <MenuItem value="reject">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#f44336',
                  }}
                />
                <Typography>Reject Application</Typography>
              </Stack>
            </MenuItem>
            <MenuItem value="on_hold">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#ff9800',
                  }}
                />
                <Typography>Put On Hold</Typography>
              </Stack>
            </MenuItem>
            <MenuItem value="consider_other_role">
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: '#9c27b0',
                  }}
                />
                <Typography>Consider for Other Role</Typography>
              </Stack>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Notes Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: '#29204C' }}>
          Evaluation Notes (Optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          name="evaluation_notes"
          value={formData.evaluation_notes}
          onChange={handleChange}
          placeholder="Add any additional notes or comments about this evaluation..."
          variant="outlined"
        />
      </Box>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={<SendIcon />}
          disabled={submitting || !formData.recommendation}
          sx={{
            bgcolor: '#29204C',
            '&:hover': { bgcolor: '#1a1430' },
            px: 4,
          }}
        >
          {submitting ? 'Submitting...' : 'Submit Evaluation'}
        </Button>
      </Box>
    </Box>
  );
};

export default EvaluationForm;
